import { AuthenticationResult, EventMessage, EventType, PopupRequest, RedirectRequest, SsoSilentRequest } from '@azure/msal-browser';
import { AuthEvent, B2cAuthorityTypeEnum, IAuthService } from '@vivli/core/infrastructure/interface';
import { BehaviorSubject } from 'rxjs';
import { LoggerService, maskEmail } from '@vivli/shared/infrastructure/service';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsalOptions } from './use-msal-options.hook';
import { useStorage } from '@vivli/shared/infrastructure/hook';
import { useMsal } from '@azure/msal-react';
import { AuthError } from '@azure/msal-browser';
import { useLogoutOnClose } from './logout-on-close.hook';

// The combined sign-up/sign-in policy lands on sign-in by default; `option=signup` deep-links straight
// to the sign-up form (the azure-ad-b2c/samples sign-up-deep-link pattern - the policy reads the
// `option` claim resolver in its first orchestration step).
const SIGN_UP_QUERY_PARAMS: Record<string, string> = { option: 'signup' };

export const useAuthService = (loggerKey = 'authService'): IAuthService => {
    const { options, isPartnerLogin } = useMsalOptions();
    const inProgressRef = useRef(false);
    // Logout-specific on purpose. inProgressRef is a general auth-interaction flag that
    // handleAuthResponse leaves true after a successful login, so using it for the ACCOUNT_REMOVED
    // guard below would suppress every cross-tab logout for the life of a signed-in tab.
    const isLoggingOutRef = useRef(false);
    const authEventsRef = useRef(new BehaviorSubject<AuthEvent>('auth-initialized'));
    const { setItem, getItem, deleteItem } = useStorage();
    const { markSessionActive, checkIfLogoutNeeded, endSession } = useLogoutOnClose();

    // Gates app rendering: the app tree stays blank until the fresh-session decision resolves, so a
    // stale session can be redirected to B2C logout without ever flashing app content or an overlay.
    const [isSessionReady, setIsSessionReady] = useState(false);

    const { instance } = useMsal();

    instance.enableAccountStorageEvents();

    // Every auth event is emitted through here so the debug console gets the full lifecycle timeline
    // without a log statement bolted onto each of the ~26 emission sites.
    const emit = (event: AuthEvent, detail?: Record<string, unknown>) => {
        LoggerService.debug(`[Auth] event: ${event}`, ...(detail ? [detail] : []));
        authEventsRef.current.next(event);
    };

    const getAccount = () => {
        return instance.getActiveAccount();
    };

    const setLoginHint = (account: any) => {
        const email = account.idTokenClaims?.['email'];
        if (email) {
            setItem('loginHint', email, 'local');
            LoggerService.debug(`[Auth] setLoginHint: stored hint for ${maskEmail(email)}`);
        } else {
            LoggerService.warn('[Auth] setLoginHint: account has no email claim, hint not stored - silent login will be unavailable');
        }
    };

    const getLoginHint = () => {
        const loginHint = getItem<string>('loginHint', 'local');
        LoggerService.debug(`[Auth] getLoginHint: ${maskEmail(loginHint)}`);
        return loginHint;
    };

    const getAuthorityUri = useCallback(
        (authorityType?: B2cAuthorityTypeEnum): string => {
            if (!options.b2cOptions || !options.b2cOptions.authorities) {
                LoggerService.warn('[Auth] getAuthorityUri: b2c options not loaded yet, returning null authority');
                return null;
            }

            const authPolicyName = options.b2cOptions?.authorities[authorityType] || options.b2cOptions?.authorities.default;
            const b2cTenant = options.b2cOptions.tenant;
            const b2cName = b2cTenant.split('.')[0];

            return `https://${b2cName}.b2clogin.com/${b2cTenant}/${authPolicyName}`;
        },
        [options.b2cOptions]
    );

    const handleAuthResult = useCallback((response: AuthenticationResult) => {
        if (response) {
            LoggerService.debug('[Auth] handleAuthResult: token acquired', {
                account: maskEmail(response.account?.username),
                expiresOn: response.expiresOn,
                fromCache: response.fromCache
            });
            emit('token-refreshed');
            return response.idToken;
        }

        LoggerService.warn('[Auth] handleAuthResult: no response returned, no token to hand back');
    }, []);

    const getTokenSilent = useCallback(async () => {
        const account = getAccount();
        if (!account) {
            LoggerService.debug('[Auth] getTokenSilent: no active account, skipping token acquisition');
            return null;
        }

        LoggerService.debug(`[Auth] getTokenSilent: acquiring token for ${maskEmail(account.username)}`);

        return instance
            .acquireTokenSilent({
                scopes: options.b2cOptions.scopes,
                account: account
            })
            .then(handleAuthResult)
            .catch((error) => {
                LoggerService.error('[Auth] getTokenSilent: acquireTokenSilent failed, clearing active account', error);
                instance.setActiveAccount(null);
                emit('silent-login-failure');

                return null;
            });
    }, [instance, options.b2cOptions.scopes, handleAuthResult]);

    const getAccountSilent = useCallback(async () => {
        const account = getAccount();
        LoggerService.debug(`[Auth] getAccountSilent: rehydrating session for ${maskEmail(account?.username)}`);

        inProgressRef.current = true;
        emit('silent-login');

        return instance
            .acquireTokenSilent({
                scopes: options.b2cOptions.scopes,
                account: account
            })
            .then((result) => {
                LoggerService.debug(`[Auth] getAccountSilent: session rehydrated for ${maskEmail(result.account?.username)}`);
                instance.setActiveAccount(result.account);
                emit('login-success');
                markSessionActive();
                inProgressRef.current = false;
            })
            .catch((error) => {
                LoggerService.error('[Auth] getAccountSilent: acquireTokenSilent failed, clearing active account', error);
                instance.setActiveAccount(null);
                emit('login-failure');
                inProgressRef.current = false;
            });
    }, [instance, options.b2cOptions.scopes, handleAuthResult]);

    const getTokenPopup = useCallback(async () => {
        LoggerService.debug('[Auth] getTokenPopup: opening token acquisition popup');

        return instance
            .acquireTokenPopup({
                scopes: options.b2cOptions.scopes,
                account: getAccount()
            })
            .then(handleAuthResult)
            .catch((error) => {
                // Log and rethrow: callers (e.g. the session-expiration prompt) still see the rejection.
                LoggerService.error('[Auth] getTokenPopup: acquireTokenPopup failed', error);
                throw error;
            });
    }, [instance, options.b2cOptions.scopes, handleAuthResult]);

    const handleAuthResponse = useCallback(
        (response: AuthenticationResult, successEvent: AuthEvent, failureEvent: AuthEvent, logMessage: string) => {
            if (response) {
                LoggerService.debug(`[Auth] handleAuthResponse: ${successEvent} for ${maskEmail(response.account?.username)}`);
                setLoginHint(response.account);
                instance.setActiveAccount(response.account);
                emit(successEvent);
                markSessionActive();
                inProgressRef.current = true;
                return response;
            } else {
                LoggerService.warn(`[Auth] handleAuthResponse: no response returned, emitting ${failureEvent} - ${logMessage}`);
                emit(failureEvent);
                inProgressRef.current = false;
                return null;
            }
        },
        [instance]
    );

    const handleLoginPopup = useCallback(
        async (
            authority: B2cAuthorityTypeEnum,
            successEvent: AuthEvent,
            failureEvent: AuthEvent,
            logMessage: string,
            extraQueryParameters?: Record<string, string>
        ) => {
            const request: PopupRequest = {
                scopes: options.b2cOptions.scopes,
                authority: getAuthorityUri(authority),
                prompt: 'select_account',
                extraQueryParameters
            };

            LoggerService.debug('[Auth] handleLoginPopup: opening login popup', {
                policy: authority,
                authority: request.authority,
                extraQueryParameters
            });

            inProgressRef.current = true;
            return instance
                .loginPopup(request)
                .then((response) => handleAuthResponse(response, successEvent, failureEvent, logMessage))
                .catch((error: AuthError) => {
                    if (error.message.includes('AADB2C90091') || error.message.includes('user_cancelled')) {
                        LoggerService.warn(`[Auth] handleLoginPopup: user cancelled the B2C popup (${error.errorCode})`);
                        emit('cancelled');
                        return;
                    } else if (error.message.includes('AADB2C90118')) {
                        // switch to change password flow
                        LoggerService.warn('[Auth] handleLoginPopup: AADB2C90118 returned, chaining into the password reset flow');
                        return updatePassword();
                    }

                    emit(failureEvent);
                    LoggerService.error(`[Auth] handleLoginPopup: ${logMessage}`, error, loggerKey);
                    inProgressRef.current = false;
                });
        },
        [instance, options.b2cOptions.scopes, getAuthorityUri, handleAuthResponse]
    );

    const handleLoginRedirect = useCallback(
        async (authority: B2cAuthorityTypeEnum, successEvent: AuthEvent, failureEvent: AuthEvent, logMessage: string) => {
            const request: RedirectRequest = {
                scopes: options.b2cOptions.scopes,
                authority: getAuthorityUri(authority),
                prompt: 'select_account'
            };

            LoggerService.debug('[Auth] handleLoginRedirect: marking session active, then redirecting to B2C', {
                policy: authority,
                authority: request.authority
            });

            // Set the per-tab active flag before navigating away so it survives the B2C redirect
            // round-trip; on return the bootstrap sees an owned session and does not log the user out.
            markSessionActive();
            inProgressRef.current = true;
            return instance.loginRedirect(request).catch((error) => {
                emit(failureEvent);
                LoggerService.error(`[Auth] handleLoginRedirect: ${logMessage}`, error, loggerKey);
            });
        },
        [instance, options.b2cOptions.scopes, getAuthorityUri]
    );

    // Ends the local half of the session: everything a successful logoutRedirect would have done on
    // its way out. Factored out so the failure paths can guarantee it too - leaving it undone is what
    // let a failed logout keep the user fully signed in (DEV-1046).
    const clearLocalSession = useCallback(async () => {
        try {
            instance.setActiveAccount(null);
            await instance.clearCache();
            LoggerService.debug('[Auth] clearLocalSession: active account and msal cache cleared');
        } catch (error) {
            LoggerService.error('[Auth] clearLocalSession: msal cache could not be cleared - tokens may survive until the next load', error, loggerKey);
        } finally {
            // Always released, even when clearCache throws: a tab that still claims an owned session
            // lets the next load adopt it back instead of re-running the stale-session logout.
            endSession();
        }
    }, [instance]);

    const logout = useCallback(
        async (redirectUrl?: string) => {
            LoggerService.debug(`[Auth] logout: starting logout redirect (postLogoutRedirectUri: ${redirectUrl ?? '(msal default)'})`);
            emit('logout');
            inProgressRef.current = true;
            isLoggingOutRef.current = true;
            deleteItem('loginHint', 'local');
            // Released before navigating away so both outcomes leave the same local state behind -
            // sessionStorage would otherwise survive the B2C round-trip and leave a stale flag.
            endSession();
            LoggerService.debug('[Auth] logout: cleared stored loginHint and released the tab session');
            return instance
                .logoutRedirect({
                    postLogoutRedirectUri: redirectUrl
                })
                .then(() => {
                    LoggerService.debug('[Auth] logout: logoutRedirect resolved, clearing active account');
                    instance.setActiveAccount(null);
                    emit('logout-success');
                    inProgressRef.current = false;
                    isLoggingOutRef.current = false;
                })
                .catch(async (error) => {
                    LoggerService.error('[Auth] logout: logoutRedirect failed', error, loggerKey);
                    // B2C never confirmed the sign-out, but the local session has to end regardless.
                    // Teardown runs before the event so the handler navigates into a clean state.
                    await clearLocalSession();
                    emit('logout-failure');
                    inProgressRef.current = false;
                    // Last, so the ACCOUNT_REMOVED raised by clearCache above is still suppressed.
                    isLoggingOutRef.current = false;
                });
        },
        [instance, clearLocalSession]
    );

    const login = useCallback(async () => {
        LoggerService.debug(`[Auth] login: starting ${isPartnerLogin ? 'partner redirect' : 'popup'} flow`);
        emit('login');

        if (isPartnerLogin) {
            return loginRedirect();
        }

        return handleLoginPopup('signUpSignIn', 'login-success', 'login-failure', 'User login failed: ');
    }, [handleLoginPopup, isPartnerLogin]);

    const loginRedirect = useCallback(async () => {
        LoggerService.debug('[Auth] loginRedirect: starting sso redirect flow');
        emit('login');

        return handleLoginRedirect('sso', 'login-success', 'login-failure', 'User login failed: ');
    }, [handleLoginRedirect, isPartnerLogin]);

    const loginSilent = useCallback(async (): Promise<boolean> => {
        const loginHint = getLoginHint();

        // no login hint found, don't attempt silent login as we don't know what user to login as
        if (!loginHint) {
            LoggerService.warn('[Auth] loginSilent: no stored loginHint, skipping silent login');
            // ensure we still throw the event to cancel the loading screen
            emit('login-failure');
            return false;
        }

        const request: SsoSilentRequest = {
            scopes: options.b2cOptions.scopes,
            authority: getAuthorityUri(),
            loginHint
        };

        LoggerService.debug(`[Auth] loginSilent: attempting ssoSilent for ${maskEmail(loginHint)}`, { authority: request.authority });

        emit('silent-login');
        inProgressRef.current = true;
        return instance
            .ssoSilent(request)
            .then((response) => {
                inProgressRef.current = false;
                if (response) {
                    LoggerService.debug(`[Auth] loginSilent: ssoSilent succeeded for ${maskEmail(response.account?.username)}`);
                    instance.setActiveAccount(response.account);
                    emit('login-success');
                    markSessionActive();
                    return true;
                }
                LoggerService.warn('[Auth] loginSilent: ssoSilent resolved with no response');
                emit('login-failure');
                return false;
            })
            .catch((error: AuthError) => {
                if (error.message.includes('interaction_required')) {
                    // do nothing, we don't force logins
                    LoggerService.warn('[Auth] loginSilent: interaction required, leaving the user signed out');
                    emit('login-failure');
                } else {
                    LoggerService.error('[Auth] loginSilent: ssoSilent failed', error);
                }
                inProgressRef.current = false;
                return false;
            });
    }, [instance, options.b2cOptions.scopes, getAuthorityUri]);

    const signUp = useCallback(async () => {
        LoggerService.debug('[Auth] signUp: starting sign-up popup flow');
        // show the "Signing up..." overlay while the sign-up popup is active
        emit('signup');

        // Sign-up runs against the same combined policy as login(), just deep-linked to the sign-up
        // form - so the account and tokens are cached under the authority everything else already
        // uses, and no follow-up login is needed to keep the user signed in.
        return handleLoginPopup('signUpSignIn', 'signup-success', 'signup-failure', 'User signup failed: ', SIGN_UP_QUERY_PARAMS);
    }, [handleLoginPopup]);

    const updatePassword = useCallback(async () => {
        LoggerService.debug('[Auth] updatePassword: starting password reset popup flow');
        emit('password-update');
        return handleLoginPopup('forgotPassword', 'password-update-success', 'password-update-failure', 'Password update failed: ');
    }, [handleLoginPopup]);

    const updateProfile = useCallback(async () => {
        LoggerService.debug('[Auth] updateProfile: starting edit profile popup flow');
        emit('profile-update');
        return handleLoginPopup('editProfile', 'profile-update-success', 'profile-update-failure', 'Profile update failed: ');
    }, [handleLoginPopup]);

    useEffect(() => {
        const callbackId = instance.addEventCallback((message: EventMessage) => {
            if (message.eventType === EventType.ACCOUNT_REMOVED) {
                // clearLocalSession() empties the msal cache, which can raise this event while a
                // logout is still in flight. Without the guard that would start a second one.
                if (isLoggingOutRef.current) {
                    LoggerService.debug('[Auth] msal ACCOUNT_REMOVED: a logout is already in progress, ignoring');
                    return;
                }
                LoggerService.debug('[Auth] msal ACCOUNT_REMOVED: account removed (likely a logout in another tab), logging out here');
                logout();
            }
        });

        return () => {
            if (callbackId) {
                instance.removeEventCallback(callbackId);
            }
        };
    }, [instance, logout]);

    useEffect(() => {
        if (inProgressRef.current) {
            LoggerService.debug('[Auth] bootstrap: an auth interaction is already in progress, skipping the session decision');
            return;
        }

        const bootstrap = async () => {
            const accounts = instance.getAllAccounts();
            LoggerService.debug(`[Auth] bootstrap: ${accounts.length} cached account(s) found`);

            // No cached account -> nothing to log out; render the app and attempt a silent login.
            if (accounts.length === 0) {
                LoggerService.debug('[Auth] bootstrap: no cached account, rendering app and attempting silent login');
                setIsSessionReady(true);
                loginSilent();
                return;
            }

            // Cached account exists: keep it only if this tab owns the session or another tab is
            // still alive. Otherwise this is a fresh browser session with leftover tokens -> log out.
            if (await checkIfLogoutNeeded()) {
                // Silent logout: clear local session, then redirect to B2C without emitting the
                // 'logout' event (no "Logging out..." overlay). The render gate is left closed so
                // the app never appears - the screen stays blank until the browser navigates away.
                LoggerService.debug(
                    '[Auth] bootstrap: stale session with leftover tokens, silently logging out (render gate stays closed)'
                );
                isLoggingOutRef.current = true;
                endSession();
                deleteItem('loginHint', 'local');
                instance.logoutRedirect().catch(async (error) => {
                    // Unhandled, this rejection leaves the render gate shut and the app blank forever.
                    // Clear locally and open the gate so the logged-out app renders instead.
                    LoggerService.error('[Auth] bootstrap: silent logoutRedirect failed', error, loggerKey);
                    await clearLocalSession();
                    isLoggingOutRef.current = false;
                    setIsSessionReady(true);
                });
                return;
            }

            LoggerService.debug('[Auth] bootstrap: session owned or adopted, rendering app and rehydrating the account');
            setIsSessionReady(true);
            getAccountSilent();
        };

        bootstrap();
    }, []);

    return useMemo(
        () => ({
            getTokenSilent,
            getTokenPopup,
            login,
            loginSilent,
            signUp,
            logout,
            updatePassword,
            updateProfile,
            getAccount,
            instance,
            authEvents: authEventsRef.current,
            isSessionReady
        }),
        [
            getTokenSilent,
            getTokenPopup,
            login,
            loginSilent,
            signUp,
            logout,
            updatePassword,
            updateProfile,
            getAccount,
            instance,
            authEventsRef,
            isSessionReady
        ]
    );
};
